import { InjectionToken } from '@angular/core';

export interface SupportConfig {
  /** e.g. `${environment.apiUrl}/company/support` */
  baseUrl: string;
  /**
   * Company/admin expose both a plain `/tickets` (full company-scoped
   * queue) and a separate `/tickets/my` (own authored only). Executor/
   * client only expose plain `/tickets`, already pre-scoped server-side to
   * the caller's own tickets - there is no `/tickets/my` route there, so
   * calling getMyTickets() would 404. Defaults to true (company/admin).
   */
  hasSeparateMyEndpoint?: boolean;
  /**
   * True only for the admin app. Admin users have no companyId/company at
   * all (admin_users is a separate table from company users), which looks
   * identical to an executor/client requester - who also has no company -
   * so responder rights cannot be inferred from the user's shape. Each app
   * must say explicitly whether it is the platform-wide responder.
   */
  isPlatformResponder?: boolean;
  /**
   * True only for the company app (staff portal: company_admin/coordinator/
   * etc). The requester-only apps (client/executor) seed their mobile-app
   * users onto the SAME MAIN company as company-app staff (see
   * demo-users.seeder.ts) - `user.company?.type === 'main'` therefore
   * cannot be used to recognize "this is a company-app operator" by
   * itself, since a client/executor requester viewing their OWN ticket
   * also has `company.type === 'main'` and `ticket.companyId ===
   * currentUserCompanyId` (the ticket inherits the creator's companyId).
   * Without this flag, a requester was wrongly granted Assign/Change
   * Status rights on their own ticket. Only company's app.config.ts sets
   * this; client/executor must never set it.
   */
  isResponderApp?: boolean;
}

export const SUPPORT_CONFIG = new InjectionToken<SupportConfig>('SUPPORT_CONFIG');

/** Minimal auth surface the support UI components need - every app's AuthService already has this. */
export interface SupportAuthUser {
  id: number;
  companyId?: number | null;
  company?: { type?: string } | null;
}

export interface SupportAuthLike {
  readonly currentUserValue: SupportAuthUser | null;
}

export const SUPPORT_AUTH = new InjectionToken<SupportAuthLike>('SUPPORT_AUTH');
